import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import ProgressiveControl from "../common/ProgressiveControl";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";
import FieldNote from "./common/FieldNote";
import WebsiteMediaInput from "./WebsiteMediaInput";

export default function WebsiteGalleryFolder() {
    const { slug } = useParams();
    const { requestAPI, showToast } = useAppContext();
    const navigate = useNavigate();
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [folder, setFolder] = useState();
    const [rows, setRows] = useState();
    const [dialog, setDialog] = useState({ visible: false });

    const loadRows = useCallback(() => {
        requestAPI({
            requestPath: "website/gallery-categories",
            requestMethod: "GET",
            setLoading,
            onRequestFailure: setError,
            onResponseReceieved: (categories, responseCode) => {
                if (!categories || responseCode !== 200) {
                    setError("Couldn't load this folder");
                    return;
                }
                const match = categories.find((item) => item.slug === slug || item.public_id === slug);
                if (!match) {
                    setError("This folder was not found");
                    return;
                }
                setFolder(match);
                requestAPI({
                    requestPath: "website/gallery-albums",
                    requestMethod: "GET",
                    onRequestFailure: setError,
                    onResponseReceieved: (albums, albumCode) => {
                        if (albums && albumCode === 200) {
                            setRows(albums.filter((item) => item.category_slug === match.slug || item.category_slug === match.public_id));
                        } else setError("Couldn't load the events in this folder");
                    },
                });
            },
        });
    }, [requestAPI, slug]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    const people = folder?.category_type === "people";
    const title = folder?.title || "Folder";

    return (
        <div className="flex flex-column h-full">
            <PageTitle title={title} />
            <TabHeader
                className="px-3 pt-3"
                title={title}
                highlights={[people ? "People shown inside this folder" : "Events inside this folder, such as Diwali or a picnic"]}
                actionItems={[
                    <Button icon="pi pi-plus" severity="warning" onClick={() => setDialog({ visible: true, record: { category_slug: folder?.slug || folder?.public_id, item_type: people ? "person" : "album", is_published: true } })} />,
                ]}
            />
            <Divider />
            {loading ? <Loading /> : error ? <NoContent error={error} /> : (
                <div className="flex-1 overflow-y-scroll flex flex-column gap-2 p-2">
                    {rows?.length ? rows.map((row) => (
                        <Row key={row.id} row={row} people={people} slug={slug} setRows={setRows} requestAPI={requestAPI} showToast={showToast} openEdit={(record) => setDialog({ visible: true, record })} />
                    )) : <NoContent />}
                </div>
            )}
            {dialog.visible ? <Editor dialog={dialog} people={people} setDialog={setDialog} setRows={setRows} requestAPI={requestAPI} showToast={showToast} /> : null}
        </div>
    );
}

function Row({ row, people, slug, setRows, requestAPI, showToast, openEdit }) {
    const navigate = useNavigate();
    const [deleting, setDeleting] = useState();
    const remove = () => {
        requestAPI({
            requestPath: `website/gallery-albums/${row.id}`,
            requestMethod: "DELETE",
            setLoading: setDeleting,
            parseResponseBody: false,
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 204) {
                    showToast({ severity: "success", summary: "Deleted", detail: people ? "Person removed" : "Event removed", life: 1000 });
                    setRows((prev) => prev?.filter((item) => item.id !== row.id));
                } else showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete !", life: 2000 });
            },
        });
    };
    const active = row.is_published;
    return (
        <div className={`flex gap-3 align-items-center border-round py-2 px-3 text-white ${active ? "bg-green-500" : "bg-red-500"}`}>
            <div className="flex flex-column flex-1 overflow-hidden">
                <span className={`${TEXT_NORMAL} font-semibold`}>{row.title || row.person_name || "Untitled"}</span>
            </div>
            {people ? null : <i className="pi pi-images cursor-pointer" title="Photos and videos" onClick={() => navigate(`/manage-website/gallery/folders/${slug}/events/${row.id}`)} />}
            <i className="pi pi-pencil cursor-pointer" onClick={() => openEdit(row)} />
            <ProgressiveControl loading={deleting} control={<ConfirmationWrapper action={remove}><i className="pi pi-trash cursor-pointer" /></ConfirmationWrapper>} />
        </div>
    );
}

function Editor({ dialog, people, setDialog, setRows, requestAPI, showToast }) {
    const [record, setRecord] = useState({ is_published: true, item_type: people ? "person" : "album", ...(dialog.record || {}) });
    const [saving, setSaving] = useState();
    const isNew = !record.id;
    const save = () => {
        const name = people ? record.person_name || record.title : record.title;
        if (!String(name || "").trim()) {
            showToast({ severity: "error", summary: "Name required", detail: people ? "Enter the person's name" : "Enter the event name", life: 2000 });
            return;
        }
        requestAPI({
            requestPath: "website/gallery-albums",
            requestMethod: isNew ? "POST" : "PATCH",
            requestPostBody: record,
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Save !", life: 2000 }),
            onResponseReceieved: (saved, responseCode) => {
                if (saved && (responseCode === 200 || responseCode === 201)) {
                    setRows((prev) => (isNew ? [...(prev || []), saved] : prev.map((item) => (item.id === saved.id ? saved : item))));
                    showToast({ severity: "success", summary: "Saved", detail: people ? "Person saved" : "Event saved", life: 1000 });
                    setDialog({ visible: false });
                } else showToast({ severity: "error", summary: "Failed", detail: saved?.error || "Failed To Save !", life: 2000 });
            },
        });
    };
    return (
        <Dialog header={isNew ? (people ? "Add a person" : "Add an event") : (people ? "Edit person" : "Edit event")} visible className="w-11" onHide={() => setDialog({ visible: false })}>
            <div className="flex flex-column">
                {people ? <PersonFields record={record} setRecord={setRecord} disabled={saving} /> : <EventFields record={record} setRecord={setRecord} disabled={saving} />}
                <div className="flex align-items-center gap-2 mt-4">
                    <Checkbox inputId="is_published" checked={record?.is_published !== false} onChange={(event) => setRecord((prev) => ({ ...prev, is_published: event.checked }))} disabled={saving} />
                    <label htmlFor="is_published" className={TEXT_SMALL}>Show this on the website</label>
                </div>
                <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={save} />
            </div>
        </Dialog>
    );
}

function EventFields({ record, setRecord, disabled }) {
    return (
        <>
            <div>
                <FloatLabel className="mt-5">
                    <InputText id="title" className="w-full" value={record?.title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, title: event.target.value }))} disabled={disabled} pt={{ root: { className: TEXT_NORMAL } }} />
                    <label htmlFor="title">Event name</label>
                </FloatLabel>
                <FieldNote text="Example: Diwali Celebration, Teachers Day, or Udaipur Picnic." />
            </div>
            <div>
                <FloatLabel className="mt-5">
                    <InputTextarea id="description" className="w-full" rows={4} autoResize value={record?.description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, description: event.target.value }))} disabled={disabled} />
                    <label htmlFor="description">Short description</label>
                </FloatLabel>
                <FieldNote text="Shown under the event name." />
            </div>
            <div>
                <FloatLabel className="mt-5">
                    <InputText id="event_date" className="w-full" value={record?.event_date ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, event_date: event.target.value }))} disabled={disabled} pt={{ root: { className: TEXT_NORMAL } }} />
                    <label htmlFor="event_date">Date</label>
                </FloatLabel>
                <FieldNote text="Example: 2025-10-20." />
            </div>
            <div>
                <FloatLabel className="mt-5">
                    <InputText id="location" className="w-full" value={record?.location ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, location: event.target.value }))} disabled={disabled} pt={{ root: { className: TEXT_NORMAL } }} />
                    <label htmlFor="location">Place</label>
                </FloatLabel>
                <FieldNote text="Example: Udaipur or SAHAS Campus." />
            </div>
            <WebsiteMediaInput label="Event cover" field="cover_image" hint="Required size: 1280 ? 800 px (16:10). A larger photo is scaled to fit this event card. The whole photo stays visible." record={record} setRecord={setRecord} disabled={disabled} />
            <div>
                <FloatLabel className="mt-5">
                    <InputText id="cover_alt" className="w-full" value={record?.cover_alt ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, cover_alt: event.target.value }))} disabled={disabled} pt={{ root: { className: TEXT_NORMAL } }} />
                    <label htmlFor="cover_alt">Cover description</label>
                </FloatLabel>
            </div>
            <div className="flex align-items-center gap-2 mt-4">
                <Checkbox inputId="is_featured" checked={!!record?.is_featured} onChange={(event) => setRecord((prev) => ({ ...prev, is_featured: event.checked }))} disabled={disabled} />
                <label htmlFor="is_featured" className={TEXT_SMALL}>Show this event as the featured album on the Gallery page</label>
            </div>
        </>
    );
}

function PersonFields({ record, setRecord, disabled }) {
    return (
        <>
            <div>
                <FloatLabel className="mt-5">
                    <InputText id="person_name" className="w-full" value={record?.person_name ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, person_name: event.target.value, title: event.target.value }))} disabled={disabled} pt={{ root: { className: TEXT_NORMAL } }} />
                    <label htmlFor="person_name">Name</label>
                </FloatLabel>
            </div>
            <div>
                <FloatLabel className="mt-5">
                    <InputText id="designation" className="w-full" value={record?.designation ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, designation: event.target.value }))} disabled={disabled} pt={{ root: { className: TEXT_NORMAL } }} />
                    <label htmlFor="designation">Role</label>
                </FloatLabel>
                <FieldNote text="Example: Senior Faculty." />
            </div>
            <div>
                <FloatLabel className="mt-5">
                    <InputText id="department" className="w-full" value={record?.department ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, department: event.target.value }))} disabled={disabled} pt={{ root: { className: TEXT_NORMAL } }} />
                    <label htmlFor="department">Department</label>
                </FloatLabel>
            </div>
            <div>
                <FloatLabel className="mt-5">
                    <InputTextarea id="description" className="w-full" rows={4} autoResize value={record?.description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, description: event.target.value }))} disabled={disabled} />
                    <label htmlFor="description">Short introduction</label>
                </FloatLabel>
            </div>
            <WebsiteMediaInput label="Photo" field="person_image" hint="Required size: 800 ? 1000 px (4:5). A larger photo is scaled to fit this people card. The whole photo stays visible." record={record} setRecord={setRecord} disabled={disabled} />
            <div>
                <FloatLabel className="mt-5">
                    <InputText id="person_image_alt" className="w-full" value={record?.person_image_alt ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, person_image_alt: event.target.value }))} disabled={disabled} pt={{ root: { className: TEXT_NORMAL } }} />
                    <label htmlFor="person_image_alt">Photo description</label>
                </FloatLabel>
            </div>
        </>
    );
}
